import React, { useState } from 'react';
import './CustomDatePicker.css';

const CustomDatePicker = ({ startDate, endDate, onChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isOpen, setIsOpen] = useState(false);

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isDateSelected = (date) => {
    if (!startDate || !endDate) return false;
    return date.getTime() === startDate.getTime() || date.getTime() === endDate.getTime();
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    // Si aucune date sélectionnée ou si on a déjà une période, recommencer avec un nouveau jour
    if (!startDate || (startDate && endDate && startDate.getTime() !== endDate.getTime())) {
      onChange([date, date]);
      return;
    }

    // Si on a un jour sélectionné (startDate === endDate), étendre ou changer
    if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
      if (date.getTime() === startDate.getTime()) {
        // Même jour cliqué, garder la sélection
        return;
      } else {
        // Nouvelle date, créer une période
        if (date < startDate) {
          onChange([date, startDate]);
        } else {
          onChange([startDate, date]);
        }
      }
    }
  };

  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isDisabled = isDateDisabled(date);
      const isSelected = isDateSelected(date);
      const isInRange = isDateInRange(date);
      const isStart = startDate && date.getTime() === startDate.getTime();
      const isEnd = endDate && date.getTime() === endDate.getTime();

      let className = 'calendar-day';
      if (isDisabled) className += ' disabled';
      if (isSelected) className += ' selected';
      if (isInRange && !isSelected && startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        className += ' in-range';
      }
      if (isStart) className += ' range-start';
      if (isEnd && startDate && endDate && startDate.getTime() !== endDate.getTime()) {
        className += ' range-end';
      }

      days.push(
        <div
          key={day}
          className={className}
          onClick={() => handleDateClick(date)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const formatDateRange = () => {
    if (!startDate && !endDate) {
      return "Choisissez vos dates";
    }
    if (startDate && endDate) {
      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
      if (days === 1) {
        return `Le ${startDate.toLocaleDateString('fr-FR')} (1 jour)`;
      }
      return `Du ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString('fr-FR')} (${days} jours)`;
    }
    return "Choisissez vos dates";
  };

  return (
    <div className="custom-date-picker">
      <div 
        className="date-picker-input-display"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="date-display">
          <span className="date-text">{formatDateRange()}</span>
          <svg 
            className={`chevron ${isOpen ? 'open' : ''}`} 
            width="20" 
            height="20" 
            viewBox="0 0 20 20"
          >
            <path 
              d="M6 8l4 4 4-4" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>
      </div>

      {isOpen && (
        <>
          <div 
            className="calendar-overlay" 
            onClick={() => setIsOpen(false)}
          ></div>
          <div className="calendar-container">
            <div className="calendar-header">
              <button 
                type="button"
                className="nav-button" 
                onClick={() => navigateMonth('prev')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path 
                    d="M10 12l-4-4 4-4" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
              <div className="month-year">
                {monthNames[currentMonth]} {currentYear}
              </div>
              <button 
                type="button"
                className="nav-button" 
                onClick={() => navigateMonth('next')}
              >
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path 
                    d="M6 4l4 4-4 4" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            </div>

            <div className="calendar-grid">
              <div className="day-names">
                {dayNames.map(day => (
                  <div key={day} className="day-name">{day}</div>
                ))}
              </div>
              <div className="calendar-days">
                {renderCalendarDays()}
              </div>
            </div>

            {startDate && (
              <div className="calendar-footer">
                {startDate && endDate && startDate.getTime() === endDate.getTime() && (
                  <p className="selection-hint">
                    Cliquez sur une autre date pour sélectionner une période
                  </p>
                )}
                <button 
                  type="button"
                  className="done-button"
                  onClick={() => setIsOpen(false)}
                >
                  Terminé
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomDatePicker;